import React, { useEffect, useRef, useState } from 'react';
import Portal from '../../Portal/Portal';
import InteractiveButton from '../../InteractiveButton/InteractiveButton';
import { ButtonType } from '../../../types/button-type';

export interface PdfViewerPage {
  id: string;
  image: string;
  isIncluded: boolean;
}

interface PdfPagesViewerModalProps {
  isVisible: boolean;
  title: string;
  pages: PdfViewerPage[];
  initialPageId: string | null;
  onClose: () => void;
  onToggleInclude: (pageId: string, isIncluded: boolean) => void;
}

// Full-screen, scrollable viewer for the pages shown as tiny iframes in the
// upload grid (ReportsImages/ReportsImagesV2) - same page URLs, just
// rendered big enough to actually read, with the same include/exclude
// checkbox carried over. Renders each page's PDF inline (iframe pointed at
// its existing signed URL, same as the small thumbnail already does), so
// nothing is ever downloaded to disk.
const PdfPagesViewerModal: React.FC<PdfPagesViewerModalProps> = ({
  isVisible,
  title,
  pages,
  initialPageId,
  onClose,
  onToggleInclude,
}) => {
  // Tracked locally rather than driven straight off `pages` - the redux
  // state behind that prop isn't updated after updateReportInclude resolves
  // (see store/models/reports.ts), so a checkbox bound directly to it would
  // appear to snap back right after being toggled.
  const [includedById, setIncludedById] = useState<Record<string, boolean>>({});
  const pageRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (isVisible) {
      setIncludedById(Object.fromEntries(pages.map((page) => [page.id, page.isIncluded])));
    }
  }, [isVisible, pages]);

  useEffect(() => {
    if (isVisible && initialPageId) {
      pageRefs.current[initialPageId]?.scrollIntoView({ block: 'start' });
    }
  }, [isVisible, initialPageId]);

  useEffect(() => {
    if (!isVisible) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const handleToggle = (pageId: string, checked: boolean) => {
    setIncludedById((prev) => ({ ...prev, [pageId]: checked }));
    onToggleInclude(pageId, checked);
  };

  return (
    <Portal>
      <div className="fixed inset-0 z-9999 flex flex-col bg-black/95">
        <div className="flex items-center justify-between gap-4 border-b border-strokedark px-4 py-3 sm:px-6">
          <h3 className="truncate text-base font-semibold text-white sm:text-lg">{title}</h3>
          <InteractiveButton type={ButtonType.Primary} text="Close" onClick={onClose} />
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-8">
          <div className="mx-auto flex max-w-4xl flex-col gap-10">
            {pages.map((page, index) => (
              <div
                key={page.id}
                ref={(node) => {
                  pageRefs.current[page.id] = node;
                }}
                className="flex scroll-mt-4 flex-col items-center gap-2"
              >
                <label className="flex cursor-pointer select-none items-center gap-2 self-start rounded bg-white px-3 py-1.5 text-sm font-medium text-black shadow">
                  <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer"
                    checked={!!includedById[page.id]}
                    onChange={(event) => handleToggle(page.id, event.target.checked)}
                  />
                  Page {index + 1} &ndash; Include in report
                </label>
                <div
                  className="w-full overflow-hidden rounded border border-strokedark bg-white shadow-lg"
                  style={{ maxWidth: 800, aspectRatio: '8.5 / 11' }}
                >
                  <iframe src={page.image} title={`Page ${index + 1}`} className="h-full w-full border-none" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default PdfPagesViewerModal;
